import { useEffect, useState } from "react";
import api from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { SanctionBadge } from "@/components/SanctionBadge";
import { VerdictPanel } from "@/pages/DashboardPage";
import { Inbox, ChevronRight } from "lucide-react";

export default function HistoryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/verdicts");
      setItems(data);
      if (data.length && !selected) setSelected(data[0]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDelete = async () => {
    if (!selected) return;
    if (!window.confirm("Eliminare definitivamente questo verdetto?")) return;
    await api.delete(`/verdicts/${selected.id}`);
    setSelected(null);
    load();
  };

  const onExportPdf = async () => {
    if (!selected) return;
    const res = await api.get(`/verdicts/${selected.id}/pdf`, { responseType: "blob" });
    downloadBlob(res.data, `verdict_${selected.id.slice(0, 8)}.pdf`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <div className="mb-8">
        <div className="font-mono-tel text-xs text-yellow-500 uppercase mb-2">[ Archivio Verdetti ]</div>
        <h1 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tight">
          Storico <span className="text-yellow-500">Decisioni</span>
        </h1>
      </div>

      {loading ? (
        <div className="font-mono-tel text-zinc-500 text-sm uppercase">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="card flex flex-col items-center justify-center py-20" data-testid="history-empty">
          <Inbox className="w-10 h-10 text-zinc-700 mb-3" />
          <div className="font-display uppercase text-zinc-400 text-xl">Nessun verdetto emesso.</div>
          <div className="text-zinc-500 text-sm mt-1">Analizza una clip per popolare l'archivio.</div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 border border-zinc-800 bg-zinc-950" data-testid="history-list">
            <div className="grid grid-cols-[1fr_auto] px-4 py-3 border-b border-zinc-800 bg-zinc-900 font-mono-tel text-[10px] text-zinc-500 uppercase tracking-widest">
              <span>Caso</span>
              <span>Sanzione</span>
            </div>
            <div className="max-h-[70vh] overflow-y-auto divide-y divide-zinc-800">
              {items.map((v) => (
                <button
                  key={v.id}
                  onClick={() => setSelected(v)}
                  className={`w-full text-left px-4 py-3 grid grid-cols-[1fr_auto_auto] items-center gap-3 hover:bg-zinc-900 transition-colors ${
                    selected?.id === v.id ? "bg-zinc-900 border-l-2 border-l-yellow-500" : ""
                  }`}
                  data-testid={`history-item-${v.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-display font-bold uppercase truncate text-white">{v.title}</div>
                    <div className="font-mono-tel text-[10px] text-zinc-500 mt-0.5">
                      {new Date(v.created_at).toLocaleString()}
                    </div>
                  </div>
                  <SanctionBadge value={v.sanzione} />
                  <ChevronRight className="w-4 h-4 text-zinc-600" />
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            {selected ? (
              <VerdictPanel verdict={selected} onExportPdf={onExportPdf} onDelete={onDelete} />
            ) : (
              <div className="border border-zinc-800 bg-zinc-950 p-10 text-center text-zinc-500">
                Seleziona un verdetto.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
